import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { OfferService } from '../OfferService';
import { toast } from 'react-toastify';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { UPDATED_OFFER } from '../../../enum/Messages';
import { formatDate, getStatusLabel } from '../../../utils/Functions';
import { Platform } from '../../../model/platform/Platform';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { ErrorToast } from '../../../utils/toasters';
import { Offer } from '../../../model/offer/Offer';
import CardLayout from '../../../shared/components/CardLayout';
import { CancellationModal, ICancellationData } from '../../../shared/components/CancellationModal';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';

export function OfferEdit() {
    const { offerStatus } = useSelector((state: any) => state.enums);

    const { offerId } = useParams<{ offerId: any }>();
    const [offer, setOffer] = useState<Offer>({} as Offer);
    const [status, setStatus] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    const navigate = useNavigate();

    function editOffer() {
        OfferService.editOffer(offerId, { ...offer, status: status })
            .then(() => {
                toast(UPDATED_OFFER);
                navigate('/offers');
            })
            .catch(err => ErrorToast(err));
    }

    useEffect(() => {
        if (!offerId) return;

        OfferService.getOffer(Number(offerId))
            .then(data => {
                setOffer(data);
            })
            .catch(err => ErrorToast(err));
    }, [offerId]);

    function handleChange(evt: any) {
        const value = evt.target.value;
        if (value === 'canceled' && offer.status !== 'canceled') {
            setShowCancelModal(true);
            return;
        }
        setStatus(value);
    }

    function confirmCancellation(data: ICancellationData) {
        setIsCancelling(true);
        OfferService.cancelOffer(offerId, { note: data.note, file: data.file })
            .then(() => {
                setShowCancelModal(false);
                toast(UPDATED_OFFER);
                navigate('/offers');
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsCancelling(false));
    }

    useEffect(() => {
        setStatus(offer.status);
    }, [offer]);

    return (
        <CardLayout classBody="fw-bold font-size-h5">
            <CancellationModal
                show={showCancelModal}
                title={`Cancel offer ${offer?.id ?? ''}`}
                onHide={() => setShowCancelModal(false)}
                onConfirm={confirmCancellation}
                isLoading={isCancelling}
            />
            <div className="d-flex flex-row">
                <div className="col-xs-6 col-md-4">
                    <p>
                        Offer ID:
                        <br />
                        <span className="text-black-50">{offer?.id}</span>
                    </p>
                    {offer?.contract && offer?.contract?.id && (
                        <p>
                            Connected Contract ID:
                            <br />
                            <span className="text-black-50">{offer?.contract?.id}</span>
                        </p>
                    )}
                    <p>
                        Date of creation:
                        <br />
                        <span className="text-black-50">{formatDate(offer?.createdAt)}</span>
                    </p>
                    {['accepted', 'completed'].includes(offer.status) && (
                        <p>
                            Date of acceptance:
                            <br />
                            <span className="text-black-50">{formatDate(offer?.acceptedDate)}</span>
                        </p>
                    )}
                    <p>
                        Company:
                        <br />
                        <span className="text-black-50">{offer?.campaign?.company?.displayName}</span>
                    </p>
                    <p>
                        Influencer:
                        <br />
                        <span className="text-black-50">{offer?.user?.displayName}</span>
                    </p>
                    {offer?.status === 'canceled' && (offer?.cancellationNote || offer?.cancellationDocumentPath) && (
                        <p>
                            Cancellation:
                            <br />
                            {offer?.cancellationNote && (
                                <span className="text-black-50 d-block" style={{ whiteSpace: 'pre-wrap' }}>
                                    {offer.cancellationNote}
                                </span>
                            )}
                            {offer?.cancellationDocumentPath && (
                                <a href={offer.cancellationDocumentPath} target="_blank" rel="noreferrer">
                                    {offer.cancellationDocumentName ?? 'Cancellation PDF'}
                                </a>
                            )}
                        </p>
                    )}
                    {offer?.purchaseOrderInfluencer && (
                        <p>
                            Purchase Order Influencer:
                            <br />
                            <span className="text-black-50">{offer?.purchaseOrderInfluencer}</span>
                        </p>
                    )}
                    {offer?.counterOfferBy ? (
                        <p>
                            Counter by:
                            <br />
                            <span className="text-black-50">{offer?.counterOfferBy?.displayName}</span>
                        </p>
                    ) : (
                        ''
                    )}
                </div>
                <div className="col-xs-6">
                    <p>
                        Campaign: <br />
                        <span className="text-black-50 word-break">
                            {offer?.campaign?.id} {offer?.campaign?.title}
                        </span>
                    </p>
                    <p>
                        Current: <br />
                        {offer?.isCurrent ? (
                            <span className="word-break text-primary">
                                <FaCheck />
                            </span>
                        ) : (
                            <span className="word-break text-danger">
                                <FaTimes />
                            </span>
                        )}
                    </p>
                    {offer?.acceptedBy && (
                        <p>
                            Accepted by:
                            <br />
                            <span className="text-black-50">
                                ({offer?.acceptedBy?.id}) {offer?.acceptedBy?.firstName ?? '-'}{' '}
                                {offer?.acceptedBy?.lastName ?? '-'}
                            </span>
                        </p>
                    )}
                    {offer?.user?.agencyCompany && (
                        <p>
                            Agency:
                            <br />
                            <span className="text-black-50">{offer?.user?.agencyCompany?.displayName}</span>
                        </p>
                    )}
                    {offer?.hasInfluencerFee && (
                        <p>
                            Influencer fee percentage:
                            <br />
                            <span className="text-black-50">{offer?.influencerFeePercentage}%</span>
                        </p>
                    )}
                    <h5 className="font-weight-bold">Platforms</h5>
                    <div className="d-flex align-items-center flex-column flex-md-row">
                        <div className="w-md-75">
                            {offer?.totalPlatforms?.map((item: Platform) => (
                                <Row key={item.id} className="align-items-center my-2">
                                    <Col md={6} xs={9} className="d-flex align-items-center">
                                        <img src={item.path} width={30} alt={item.name} />
                                        <span className="mb-0 ml-2">{item.name}</span>
                                    </Col>
                                    <Col md={6} xs={2}>
                                        <span className="text-black-50 mb-0 ml-6">x{item.nbPostsTotal}</span>
                                    </Col>
                                </Row>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {offer?.isCurrent && (
                <>
                    <div>
                        <Form.Label>Status:</Form.Label>
                        <SingleSelectDropdown
                            placeholder="Status"
                            clearable={false}
                            options={offerStatus.map((option: string) => ({
                                value: option,
                                label: getStatusLabel(option),
                            }))}
                            selected={status ?? ''}
                            onChange={value => handleChange({ target: { name: 'status', value } })}
                        />
                    </div>
                    <Button className="col-md-1 col-sm-12 mt-4" onClick={() => editOffer()} size="lg" type="submit">
                        Save
                    </Button>
                </>
            )}
        </CardLayout>
    );
}
